import { Link, useParams } from '@tanstack/react-router';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Card } from '@cortex/web-design-system/components/ui/Card';
import { useKnowledgeMetaDetail } from '../hooks/useKnowledgeMeta.js';

/**
 * @graph-node {Page}
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business Meta公式情報の詳細ページ。記事の全文と出典URLを表示する。Meta 側で削除済みの記事にはバッジを出し、過去の判断根拠として読んでいることが分かるようにする。
 * @graph-connects useKnowledgeMetaDetail [calls] 記事の全文取得
 */
export function KnowledgeMetaDetailPage() {
  const { t } = useTranslation();
  const { docId } = useParams({ from: '/meta-docs/$docId' });
  const { data, isLoading, error } = useKnowledgeMetaDetail(docId);

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-10">
      <Link
        to="/meta-docs"
        className="mb-6 inline-flex items-center gap-1 text-sm text-[var(--color-text-muted)] hover:underline"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        {t('knowledgeMeta.backToSearch')}
      </Link>

      {isLoading && (
        <div className="py-16 text-center text-sm text-[var(--color-text-muted)]">
          {t('app.loading')}
        </div>
      )}

      {error && (
        <Card className="border-[var(--color-error)]/20 p-6 text-center">
          <p className="text-sm text-[var(--color-error)]">{t('knowledgeMeta.detailError')}</p>
        </Card>
      )}

      {data && (
        <Card className="p-6">
          <h1 className="mb-3 text-xl font-bold text-[var(--color-text-primary)]">{data.title}</h1>

          <div className="mb-4 flex flex-wrap items-center gap-2 border-b border-[var(--color-border)] pb-4 text-xs text-[var(--color-text-muted)]">
            <span className="rounded bg-[var(--color-bg-tertiary)] px-2 py-0.5 font-semibold">
              {t(`knowledgeMeta.section.${data.section}`)}
            </span>
            {data.sourceUpdatedAt !== null && (
              <span>
                {t('knowledgeMeta.metaUpdated', { date: data.sourceUpdatedAt.slice(0, 10) })}
              </span>
            )}
            <span className="tabular-nums">
              {t('knowledgeMeta.charCount', { count: data.charCount })}
            </span>
            {!data.isActive && (
              <span className="rounded bg-[var(--color-warning)]/15 px-2 py-0.5 font-semibold text-[var(--color-warning)]">
                {t('knowledgeMeta.deleted')}
              </span>
            )}
          </div>

          {!data.isActive && (
            <p className="mb-4 rounded bg-[var(--color-warning)]/10 px-3 py-2 text-xs text-[var(--color-warning)]">
              {t('knowledgeMeta.deletedNote')}
            </p>
          )}

          <div className="mb-6 text-sm leading-relaxed whitespace-pre-wrap text-[var(--color-text-primary)]">
            {data.body}
          </div>

          <a
            href={data.sourceUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 border-t border-[var(--color-border)] pt-4 text-xs break-all text-[var(--color-text-muted)] hover:underline"
          >
            <ExternalLink className="h-3 w-3 shrink-0" aria-hidden="true" />
            {t('knowledgeMeta.source')} {data.sourceUrl}
          </a>
        </Card>
      )}
    </div>
  );
}
